import { Content } from 'mdast';
import * as React from 'react';
import ReactMarkdown from 'react-markdown';

import { Link, LinkProps } from '../link';
import { styled } from '../styled';
import { Typography, TypographyProps } from '../typography';

export interface MarkdownProps extends TypographyProps {
    renderers?: ReactMarkdown.ReactMarkdownProps['renderers'];
    blockText?: boolean;
    children?: string;
    linkComponent?: React.ComponentType<LinkProps>;
}

/** only allow a limited list of html-tags as valid tags */
const allowNode = (node: Content) => {
    if (node.type === 'html') {
        if (node.value && /<\/?(li|br|ul|p|h[1-6]|b) *\/*>/.test(node.value)) {
            return true;
        }

        return false;
    }

    return true;
};

const StyledReactMarkdown = styled(
    ReactMarkdown ?? <></>
)<ReactMarkdown.ReactMarkdownProps>`
    > p {
        margin-top: 0;
    }
    ul {
        margin-top: 0;
        padding-left: 0;
    }
    > p:only-child {
        margin: 0;
    }
    pre {
        white-space: pre-line;
    }
    > ul > li,
    > div > ul > li,
    > span > li {
        margin-left: ${({ theme }) => theme.spacing(2)};
        padding-left: ${({ theme }) => theme.spacing(0.5)};
    }
`;

const SpanRoot = (props: { className?: string; children: React.ReactNode }) => (
    <span {...props} />
);

const DivRoot = (props: { className?: string; children: React.ReactNode }) => (
    <div {...props} />
);

const MarkdownLink = (props: LinkProps) => {
    return <Link {...props} target="_blank" rel="noreferrer" />;
};

export const Markdown = (props: MarkdownProps) => {
    const {
        blockText,
        children,
        renderers: renderersProps,
        linkComponent,
        ...typographyProps
    } = props;
    const renderers = {
        root: blockText ? DivRoot : SpanRoot,
        link: linkComponent ?? MarkdownLink,
        ...(renderersProps ?? {})
    };

    return children ? (
        <Typography component="span" {...typographyProps}>
            <StyledReactMarkdown
                source={children}
                escapeHtml={false}
                allowNode={allowNode}
                renderers={renderers}
            />
        </Typography>
    ) : null;
};

Markdown.displayName = 'Markdown';

const MARKDOWN_CHARACTERS = [
    '\\',
    '`',
    '*',
    '_',
    '{',
    '}',
    '[',
    ']',
    '(',
    ')',
    '#',
    '+',
    '-',
    '.',
    '!'
];

export function escape(text: string): string {
    return text.replace(
        new RegExp(`([${MARKDOWN_CHARACTERS.join('\\')}])`, 'g'),
        '\\$1'
    );
}
